/**
 * service-registry.ts
 * Single source of truth for what the app offers. Each tile belongs to one
 * hub of the bottom tab bar (Campo, Yo); HubView.vue renders a hub's
 * tiles and has no per-service branching. Adding a new service means adding
 * one entry here with its hub.
 *
 * Visibility follows the user's capabilities (operator-tabs-by-role): a tile
 * shows only when the user holds the capability its screen's endpoint
 * requires, and a hub (tab) shows only when at least one of its available
 * tiles does, except Yo, which always shows because it holds the sign-out. The backend guards stay the authority; this only keeps the UI
 * from offering screens that would answer 403.
 *
 * 2026-10-31 (operator-home-hoy) — the Home launcher grid that mixed field
 * and HR tiles was replaced by the Hoy feed plus these hubs, so every tile
 * now declares the hub it lives in.
 *
 * operator-scan-tab (owner decision 2026-10-31): the QR scanner took the
 * Equipo tab's place in the bar ("no creo que la persona use para ver el
 * organigrama"); the org chart moved into Yo.
 *
 * operator-app-unificada T3 (owner decision 2026-11-04, "escanear como
 * atajo"): the scanner is no longer a tab but Campo's first entry and an
 * Inicio shortcut; Yo is titled "Mi legajo"; absences left Yo to become the
 * Solicitudes tab (SOLICITUDES_CAPABILITIES). owningTab() tells which tab
 * a screen belongs to, for the TabBar and the AppBar's back fallback.
 */
import type { Component } from 'vue';
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router';
import { hasAnyCapability, hasCapability } from './capabilities.js';
import { Wrench, Route, Fuel, ScanLine, CalendarClock, FileText, Network, ShieldCheck, Boxes, GraduationCap } from '@lucide/vue';
import { ABSENCE_REQUESTS_CAPABILITY, PURCHASE_REQUESTS_CAPABILITY, SERVICE_REQUESTS_CAPABILITY } from './my-requests.js';

export type HubSection = 'campo' | 'yo';

export interface ServiceTile {
  readonly id: string;
  readonly label: string;
  readonly icon: Component;
  readonly routeName: string | null;
  readonly status: 'available' | 'coming-soon';
  readonly section: HubSection;
  /**
   * Capability the screen's own read endpoint requires (its backend guard).
   * Null for coming-soon entries, which never decide a hub's visibility.
   */
  readonly requiredCapability: string | readonly string[] | null;
  /** One muted line under the label in the hub list (static, never fetched). */
  readonly hint?: string;
}

export interface Hub {
  readonly section: HubSection;
  readonly title: string;
  readonly routeName: string;
}

export const HUBS: readonly Hub[] = [
  { section: 'campo', title: 'Campo', routeName: 'hub-campo' },
  { section: 'yo', title: 'Mi legajo', routeName: 'hub-yo' },
] as const;

/** The scanner (/escanear): same capability as GET /servicios/fleet-qr/:token. */
export const SCAN_CAPABILITY = 'locx.activo.leer';

export const SERVICE_TILES: readonly ServiceTile[] = [
  // operator-app-unificada T3 — the scanner, first in Campo now that it is not a tab.
  { id: 'escanear', label: 'Escanear equipo', icon: ScanLine, routeName: 'qr-scan', status: 'available', section: 'campo', requiredCapability: SCAN_CAPABILITY, hint: 'Leé el QR de un equipo' },
  { id: 'fallas', label: 'Fallas', icon: Wrench, routeName: 'fallas-list', status: 'available', section: 'campo', requiredCapability: 'locx.falla.leer' },
  { id: 'viajes', label: 'Gerenciamiento de viaje', icon: Route, routeName: 'trips-list', status: 'available', section: 'campo', requiredCapability: 'locx.viaje.leer' },
  { id: 'combustible', label: 'Carga de combustible', icon: Fuel, routeName: 'fuel-loads-list', status: 'available', section: 'campo', requiredCapability: 'locx.parte.leer' },
  // odd/tasks/operator-panol.md — scan an article and act on it; the scan endpoint takes either stock read.
  { id: 'panol', label: 'Pañol', icon: Boxes, routeName: 'panol', status: 'available', section: 'campo', requiredCapability: ['locx.stock.leer', 'requi.stock.leer'] },
  { id: 'calendario', label: 'Mi diagrama', icon: CalendarClock, routeName: 'mi-calendario', status: 'available', section: 'yo', requiredCapability: 'self.legajo.leer', hint: 'Tus días de trabajo y de descanso' },
  { id: 'documentos', label: 'Mis documentos', icon: FileText, routeName: 'mis-documentos', status: 'available', section: 'yo', requiredCapability: 'self.documento.leer', hint: 'Tu documentación y el certificado laboral' },
  // nomi-epp-firma T4 — the Campo "EPP" coming-soon slot became the employee's
  // own deliveries, signature and constancia; it lives next to Mis documentos.
  { id: 'epp', label: 'Mis EPP', icon: ShieldCheck, routeName: 'mis-epp', status: 'available', section: 'yo', requiredCapability: 'self.documento.leer', hint: 'Entregas, firma y constancia' },
  // operator-app-unificada T6 — same key as Mis documentos: trainings are the worker's own documentation.
  { id: 'capacitaciones', label: 'Mis capacitaciones', icon: GraduationCap, routeName: 'mis-capacitaciones', status: 'available', section: 'yo', requiredCapability: 'self.documento.leer', hint: 'Tus cursos y cuándo vencen' },
  { id: 'organigrama', label: 'Mi equipo', icon: Network, routeName: 'organigrama', status: 'available', section: 'yo', requiredCapability: 'self.organigrama.leer', hint: 'Tu jefe directo y tu cuadrilla' },
] as const;

/**
 * Voice intake accepts any intent the user may create (VOICE_INTAKE_INTENT_CAPABILITY
 * in packages/modules/servicios/src/http/voz/voice-intake.routes.ts).
 */
export const VOICE_INTAKE_CAPABILITIES: readonly string[] = ['locx.falla.crear', 'locx.servicio.crear'];

function canOpen(tile: ServiceTile, capabilities: readonly string[]): boolean {
  const required = tile.requiredCapability;
  if (required === null) return false;
  return typeof required === 'string' ? hasCapability(capabilities, required) : hasAnyCapability(capabilities, required);
}

/** The hub's tiles this user can open, plus its coming-soon entries when the hub has any. */
export function tilesFor(section: HubSection, capabilities: readonly string[]): readonly ServiceTile[] {
  const inHub = SERVICE_TILES.filter((tile) => tile.section === section);
  if (!inHub.some((tile) => canOpen(tile, capabilities))) return [];
  return inHub.filter((tile) => tile.status === 'coming-soon' || canOpen(tile, capabilities));
}

/**
 * A hub shows only when the user's capabilities open at least one of its
 * services. Mi legajo (section 'yo') used to stay visible for "Cerrar sesión";
 * that now lives in the profile sheet behind the AppBar avatar.
 */
export function isHubVisible(section: HubSection, capabilities: readonly string[]): boolean {
  return tilesFor(section, capabilities).length > 0;
}

export function visibleHubs(capabilities: readonly string[]): readonly Hub[] {
  return HUBS.filter((hub) => isHubVisible(hub.section, capabilities));
}

export function canUseVoice(capabilities: readonly string[]): boolean {
  return hasAnyCapability(capabilities, VOICE_INTAKE_CAPABILITIES);
}

export function canScan(capabilities: readonly string[]): boolean {
  return hasCapability(capabilities, SCAN_CAPABILITY);
}

/**
 * The Solicitudes tab (operator-app-unificada T3): everything the user asked
 * for — absences, services, purchases (lib/my-requests.ts). It opens with the
 * guard of any of its three lists; without one the screen could only answer
 * 403. Campo's "Servicios Solicitados" and "Pedidos de repuestos" moved here
 * (owner, 2026-11-04).
 */
export const SOLICITUDES_CAPABILITIES: readonly string[] = [
  ABSENCE_REQUESTS_CAPABILITY,
  SERVICE_REQUESTS_CAPABILITY,
  PURCHASE_REQUESTS_CAPABILITY,
];

export function canRequest(capabilities: readonly string[]): boolean {
  return hasAnyCapability(capabilities, SOLICITUDES_CAPABILITIES);
}

/**
 * Route name of the tab that owns a screen: an explicit `meta.tab`, else a
 * tab root (`meta.hub`) is its own tab, else the hub of the service whose
 * list path the screen sits under (/fallas/123 → Campo). Null when none.
 */
export function owningTab(route: RouteLocationNormalizedLoaded, router: Router): string | null {
  if (typeof route.meta['tab'] === 'string') return route.meta['tab'];
  if (route.meta['hub'] === true && typeof route.name === 'string') return route.name;
  const owner = SERVICE_TILES.find((tile) => {
    if (!tile.routeName || !router.hasRoute(tile.routeName)) return false;
    const base = router.resolve({ name: tile.routeName }).path;
    return route.path === base || route.path.startsWith(`${base}/`);
  });
  return owner ? hubFor(owner.section).routeName : null;
}

export function hubFor(section: HubSection): Hub {
  const hub = HUBS.find((h) => h.section === section);
  if (!hub) throw new Error(`Unknown hub section: ${section}`);
  return hub;
}
